import { Control, useController, useFieldArray } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Alert, Stack } from "@mui/material";
import React, { useMemo } from "react";
import BaseMaterialButton from "@reusables/BaseMaterialButton";
import { BatchNumberAutocompleteOption } from "./index";
import { ReactComponent as DeleteSVG } from "@assets/icons/ic_trash.svg";
import BaseInputsGrid from "@reusables/BaseInputsGrid";
import BaseTooltip from "@/components/reusables/BaseTooltip";
import { MessageSquareText } from "lucide-react";
import BatchItemFields from "./BatchItemFields";
import { BatchNumbersScheme } from "./types";

export function BatchGroup({ index, control, preview, batchNumbersOptions }: {
    index: number,
    control: Control<BatchNumbersScheme>,
    preview?: boolean;
    batchNumbersOptions?: BatchNumberAutocompleteOption[];
}) {
    const { t } = useTranslation("", { keyPrefix: "sales.picking.modals.batchNumbers" });

    const { field: line, fieldState: { error } } = useController({
        control,
        name: `lines.${index}`
    });

    const batchNumbersError = useMemo(() => {
        if (error && "batch_numbers" in error) {
            return (error.batch_numbers as { root?: { message: string } } & { message?: string });
        }
    }, [error]);

    const { fields: batchNumbers, append, remove } = useFieldArray({
        control,
        name: `lines.${index}.batch_numbers` as "lines.0.batch_numbers"
    });

    return (
        <div>
            <div className="flex items-center mb-[32px]">
                <div className="flex justify-start items-center grow">
                    <div className="text-lg font-semibold text-accent">{line.value.product.name}</div>
                    <div className="ml-2">
                        {line.value.comment && (
                            <BaseTooltip
                                title={line.value.comment}
                                placement="bottom"
                            >
                                <MessageSquareText
                                    className="w-5 ml-2 text-[#5A5587] hover:text-accent cursor-pointer" />
                            </BaseTooltip>
                        )}
                    </div>
                </div>
                {
                    !preview &&
                    <Stack
                        direction={"row"}
                        alignItems={"center"}
                        spacing={1}
                    >
                        {/* Batches for the product left (limited by picked quantity) */}
                        <div
                            className={`opacity-50 ${batchNumbers.length > line.value.picked_quantity ? "text-[#E25C5C]" : ""}`}>
                            ({batchNumbers.length} / {line.value.picked_quantity})
                        </div>
                        {/* Button to append batches to the product */}
                        <BaseMaterialButton type="button" onClick={() => {
                            append({
                                batch_number: ""
                            });
                        }}>
                            {t("buttons.addBatchNumber")}
                        </BaseMaterialButton>
                    </Stack>
                }
            </div>

            {/* Error, if the batches number exceeds the picked quantity */}
            {
                !!batchNumbersError?.root &&
                <Alert severity="error" className="mb-[32px]">
                    {batchNumbersError.root?.message}
                </Alert>
            }

            <div className="space-y-[8px]">
                {
                    batchNumbers.map((batch, batchIndex) => (
                        <Stack
                            key={batch.id}
                            direction={"row"}
                            alignItems={"center"}
                            spacing={1}
                        >
                            <div className="w-full">
                                <BaseInputsGrid cols={2} gap={8}>
                                    <BatchItemFields
                                        batchNumber={`lines.${index}.batch_numbers.${batchIndex}`}
                                        availableBatchNumbers={batchNumbersOptions}
                                        control={control}
                                        disabled={preview}
                                    />
                                </BaseInputsGrid>
                            </div>
                            {
                                !preview &&
                                <span>
                                    <DeleteSVG
                                        className="text-tables-secondaryIcon hover:text-tables-highlightedIcon cursor-pointer"
                                        onClick={() => {
                                            remove(batchIndex);
                                        }}
                                    />
                                </span>
                            }

                        </Stack>
                    ))
                }
            </div>
        </div>
    );

}